import { useEffect } from 'react';
import cn from 'classnames';

import { beFindReaderV3 } from 'actions/pos/beFindReader.action';
import { buildPaymentMethodOptions } from 'bl/client/addCredit.bl';
import Icon from 'components/generic/Icon.component';
import { CardReadersList } from 'components/pos/CardReadersList.component';
import DEPOSIT_SOURCE from 'constants/payment/depositSource.const';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { firstOf } from 'utils/arrays/elements.util';
import noNilArray from 'utils/arrays/noNilArray.util';
import sortById from 'utils/arrays/sortById.util';
import fromObjectToEntries from 'utils/converters/object/fromObjectToEntries.util';

import CN from './VoucherPaymentSection.module.scss';

export const VoucherPaymentSection = ({ posFlow, source, onSelect, onReaderStateChange }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const isPosEnabled = posFlow === true; // TODO: remove in GS-5744 when posFlow will be always one
  const cardSource = isPosEnabled ? DEPOSIT_SOURCE.pos : DEPOSIT_SOURCE.card;
  const paymentMethodOptions = buildPaymentMethodOptions(posFlow, t);

  const readersResult = usePrepareReadOnly(beFindReaderV3, { isMissingParams: !isPosEnabled });
  const readersJson = readersResult?.json;
  const isReadersLoading = readersResult?.isLoading;
  const readers = Array.isArray(readersJson) ? readersJson : [];
  const generalReaderStatus = Array.isArray(readersJson) ? undefined : readersJson?.generalReaderStatus;

  const sortedReaders = [...noNilArray(readers)].sort(sortById);
  const onlineReaders = sortedReaders.filter((r) => POS_READER_STATUS.online === r.status);
  const initialReader = onlineReaders.find((r) => r.isMain) ?? firstOf(onlineReaders);

  const [selectedReader, setSelectedReader] = useResettingState(initialReader);
  const { val: isNonSlickSelected, yay: selectNonSlick, nay: unSelectedNonSlick } = useTip(false);

  const isPOSEnabledAndSelected = isPosEnabled && source === cardSource;

  useEffect(() => {
    if (!isPosEnabled || isReadersLoading || onlineReaders.length) return;
    selectNonSlick();
    setSelectedReader(null);
  }, [isPosEnabled, isReadersLoading, onlineReaders.length, selectNonSlick, setSelectedReader]);

  useEffect(() => {
    onReaderStateChange?.({ selectedReader, isNonSlickSelected });
  }, [selectedReader, isNonSlickSelected, onReaderStateChange]);

  const handleSelectReader = (reader) => {
    unSelectedNonSlick();
    setSelectedReader(reader);
  };

  const handleSelectNonSlick = () => {
    selectNonSlick();
    setSelectedReader(null);
  };

  return (
    <div
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <span className={CN.paymentMethodLabel}>{t('scheduler.voucherView.paymentMethodSectionTitle')}</span>
      <div className={CN.paymentMethodOptions}>
        {fromObjectToEntries(paymentMethodOptions).map(([key, item]) => (
          <button key={key} type="button" data-bem="VoucherView__payment-option" className={CN.paymentOption} onClick={() => onSelect(key)} data-selected={source === key}>
            <Icon variant={item.icon} width="lg" isCentered />
            <span className={CN.paymentOptionText}>{item.label}</span>
          </button>
        ))}
      </div>
      {isPOSEnabledAndSelected && (
        <CardReadersList
          value={selectedReader}
          onSelect={handleSelectReader}
          isNonSlickSelected={isNonSlickSelected}
          onNonSlickSelect={handleSelectNonSlick}
          isLoading={isReadersLoading}
          readers={readers}
          generalReaderStatus={generalReaderStatus}
          variant="depositInline"
        />
      )}
    </div>
  );
};
